/**
 * Which storey an element belongs to — the floor plan's rule
 * (web-app/src/utils/storyOptions.js re-exports it), kept here so the
 * inspection report draws a storey's walls by the same rule on the server.
 * Pure.
 */

// THE story-match predicate: true when the element belongs on the given
// story. No selection (null/undefined) matches everything; elements with no
// finite `.story` are visible on every story rather than silently dropped.
const matchesStory = (el, story) =>
  story == null || !Number.isFinite(el?.story) || el.story === story;

// ---------------------------------------------------------------------------
// Which storeys a STRUCTURE wall belongs to
//
// A structure wall is one record stamped on one storey, but a double-height
// wall — a stairwell or loft wall the pipeline raised through the storey
// above — is sat on by an upstairs room through `alignment.structureWallId`.
// It belongs to both floors: it is drawn on both, and a wall-length edit on
// either floor that moves it reaches the other
// (`web-app/src/utils/stretchStoryScope.js`).
// "Belongs to" is therefore the wall's own stamp PLUS the storey of every room
// aligned to it, and nothing geometric — stacked walls on separate records
// stay separate records, so lengthening the ground floor leaves the floor
// above where it was.
//
// Only STAMPED walls get an entry. An unstamped wall keeps `matchesStory`'s
// everywhere rule: building it a set from its rooms alone would narrow a wall
// visible on every floor down to the floors that happen to claim it.
// ---------------------------------------------------------------------------
const buildWallStoryLookup = (rooms, walls) => {
  const lookup = new Map();
  for (const wall of walls || []) {
    if (wall?.id && Number.isFinite(wall.story))
      lookup.set(wall.id, new Set([wall.story]));
  }
  for (const room of rooms || []) {
    if (!Number.isFinite(room?.story)) continue;
    for (const roomWall of room.walls || []) {
      const stories = lookup.get(roomWall?.alignment?.structureWallId);
      if (stories) stories.add(room.story);
    }
  }
  return lookup;
};

// `matchesStory` for a structure wall: its storey set when the lookup has
// one, its own stamp otherwise (unstamped, or no lookup supplied).
const wallMatchesStory = (wall, story, lookup) => {
  if (story == null) return true;
  const stories = lookup?.get(wall?.id);
  return stories ? stories.has(story) : matchesStory(wall, story);
};

module.exports = { matchesStory, buildWallStoryLookup, wallMatchesStory };
